import { FC, useEffect, useRef, useState } from "react";
import DashboardContentLayout from "../components/dashboard-content-layout";
import FilterRow from "../components/generic-table/filter-row";
import sanitizeFilterValue from "../../../utils/sanitize-filter-value";
import { makeSxStyles, useDebounce } from "ui";
import {
  Nullable,
  notificationsTypeSelectOptions,
  useAdminNotificationControllerGetAll,
  useAdminServiceAlertsControllerFindMany,
} from "service";
import NotificationsManagementTable from "./notifications-management-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortOptions } from "../components/generic-table/generic-table";
import {
  NotificationsDeliveryEnum,
  NotificationsDeliveryPartEnum,
  NotificationsDeliveryTabEnum,
  NotificationsServiceAlertStatusEnum,
  NotificationsTypeEnum,
  NotificationTypeArray,
  notificationsDeliveryPartEnumValues,
  notificationsDeliveryPartSelectOptions,
  notificationsDeliveryTabEnumFilterMap,
  notificationsServiceAlertStatusEnumValues,
  notificationsServiceAlertStatusSelectOptions,
  notificationsTypeEnumValues,
} from "service/types/enum/notifications-enum";
import { AdRoutes } from "../../../constants/ad-routes";
import { Box, Tab, Tabs } from "@mui/material";
import { Add } from "@mui/icons-material";
import NotificationsManagementServiceAlertsTable from "./notifications-management-service-alerts-table";
import RouteNotificationsTable from "../../../features/notifications/components/route-notifications-table";
import { useEventNotificationsList } from "../../../features/notifications/hooks/use-event-notifications";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import HeaderStandardActions from "../../../shared/components/header-items/HeaderStandardActions";
import SocketContextProvider from "../../../web-sockets/web-socket-provider";
import { usePersistedSearchParams } from "../../../hooks/use-persisted-search-params";
import EmptyState, {
  noResultsEmptyState,
} from "@/shared/components/EmptyState";
import { useListEmptyState } from "@/shared/hooks/useListEmptyState";

const NotificationsManagementPage: FC = () => {
  const { debounce } = useDebounce(475);
  const navigate = useNavigate();
  const styles = useStyles();
  const [params, setSearchParams] = useSearchParams();

  // Persist search params for breadcrumb navigation
  usePersistedSearchParams();

  const [selectedTab, setSelectedTab] = useState<NotificationsDeliveryTabEnum>(
    params.get("tab")
      ? (params.get("tab") as NotificationsDeliveryTabEnum)
      : NotificationsDeliveryTabEnum.oneTime,
  );
  const [page, setPage] = useState<number>(
    params.get("page") ? parseInt(params.get("page") as string) : 1,
  );
  const [search, setSearch] = useState<string>(params.get("search") || "");
  const [limit, setLimit] = useState<number>(
    params.get("limit") ? parseInt(params.get("limit") as string) : 50,
  );

  const [sortOptions, setSortOptions] = useState<Nullable<SortOptions>>(
    params.get("sortBy")
      ? {
          sortBy: params.get("sortBy") || "",
          direction: (params.get("direction") || "") as "asc" | "desc",
        }
      : null,
  );
  const [serviceAlertSortOptions, setServiceAlertSortOptions] =
    useState<Nullable<SortOptions>>(null);
  const [routeNotificationsSortOptions, setRouteNotificationsSortOptions] =
    useState<Nullable<SortOptions>>(
      params.get("sortBy")
        ? {
            sortBy: params.get("sortBy") || "",
            direction: (params.get("direction") || "") as "asc" | "desc",
          }
        : null,
    );
  const [notificationsType, setNotificationsType] = useState<
    NotificationsTypeEnum | undefined
  >(
    params.get("type")
      ? (params.get("type") as NotificationsTypeEnum)
      : undefined,
  );
  const [notificationsStatus, setNotificationsStatus] = useState<
    NotificationsServiceAlertStatusEnum | undefined
  >(
    params.get("status")
      ? (params.get("status") as NotificationsServiceAlertStatusEnum)
      : undefined,
  );
  const [notificationsDeliverySelect, setNotificationsDeliverySelect] =
    useState<NotificationsDeliveryPartEnum | undefined>(
      NotificationsDeliveryPartEnum.delivery,
    );
  const [notificationsDelivery, setNotificationsDelivery] = useState<
    NotificationsDeliveryEnum[] | undefined
  >(notificationsDeliveryTabEnumFilterMap[selectedTab]);
  const onSearch = (searchValue: string) => {
    debounce(() => {
      setSearch(searchValue);
      // Reset to page 1 when search changes
      setPage(1);
      // Note: FilterRow already updates the 'search' param in the URL
    });
  };

  // Track previous values to avoid unnecessary URL updates on mount
  const prevPageRef = useRef(page);
  const prevLimitRef = useRef(limit);

  // Sync page and limit changes to URL (only when they actually change)
  useEffect(() => {
    // Skip if values haven't changed from their previous state
    if (prevPageRef.current === page && prevLimitRef.current === limit) {
      return;
    }
    prevPageRef.current = page;
    prevLimitRef.current = limit;

    setSearchParams((prev) => {
      if (page > 1) {
        prev.set("page", page.toString());
      } else {
        prev.delete("page");
      }
      if (limit !== 50) {
        prev.set("limit", limit.toString());
      } else {
        prev.delete("limit");
      }
      return prev;
    });
  }, [page, limit, setSearchParams]);

  const { isLoading, isError, refetch, data } =
    useAdminNotificationControllerGetAll({
      queryParams: {
        page,
        limit,
        search,
        sort: sortOptions?.sortBy as
          | "updatedAt"
          | "lastSent"
          | "date"
          | undefined,
        order: sortOptions?.direction,
        type: notificationsType,
        deliveryType: notificationsDelivery,
      },
    });

  const {
    isLoading: serviceAlertsIsLoading,
    isError: serviceAlertsIsError,
    refetch: serviceAlertsRefetch,
    data: serviceAlertsData,
  } = useAdminServiceAlertsControllerFindMany({
    queryParams: {
      page,
      limit,
      search,
      sort: serviceAlertSortOptions?.sortBy as
        | "driverName"
        | "customerName"
        | "lastSent"
        | "status"
        | undefined,
      order: serviceAlertSortOptions?.direction,
      status: notificationsStatus as "sent" | "notSentYet" | undefined,
    },
  });

  const {
    isLoading: eventNotificationsIsLoading,
    isError: eventNotificationsIsError,
    refetch: eventNotificationsRefetch,
    data: eventNotificationsData,
  } = useEventNotificationsList({
    page,
    limit,
    search,
    sort: routeNotificationsSortOptions?.sortBy as
      | "updatedAt"
      | "orderIndex"
      | "description"
      | undefined,
    order: routeNotificationsSortOptions?.direction,
    notificationType: notificationsType
      ? ([notificationsType] as NotificationTypeArray)
      : undefined,
  });

  const refetchServiceAlerts = () => {
    serviceAlertsRefetch();
  };

  useEffect(() => {
    switch (selectedTab) {
      case NotificationsDeliveryTabEnum.oneTime:
        setNotificationsDeliverySelect(NotificationsDeliveryPartEnum.delivery);
        setNotificationsDelivery([
          NotificationsDeliveryEnum.instant,
          NotificationsDeliveryEnum.scheduled,
        ]);
        break;
      case NotificationsDeliveryTabEnum.recurring:
        setNotificationsDelivery([NotificationsDeliveryEnum.recurring]);
        break;
      case NotificationsDeliveryTabEnum.routeNotifications:
        // Route notifications don't use delivery filters
        break;
      case NotificationsDeliveryTabEnum.serviceAlerts:
        // Service alerts don't use delivery filters
        break;
      case NotificationsDeliveryTabEnum.automated:
        // Automated notifications don't use delivery filters
        break;
    }
  }, [selectedTab]);

  const activeIsLoading =
    selectedTab === NotificationsDeliveryTabEnum.serviceAlerts
      ? serviceAlertsIsLoading
      : selectedTab === NotificationsDeliveryTabEnum.routeNotifications
        ? eventNotificationsIsLoading
        : isLoading;

  // Total dataset size for the active tab, not the current page's row
  // count — a retained page number that's out of range for the active
  // tab/filter would otherwise return zero rows and read as a genuinely
  // empty dataset, hiding pagination along with the rest of the toolbar.
  const activeTotalCount =
    selectedTab === NotificationsDeliveryTabEnum.serviceAlerts
      ? (serviceAlertsData?.metaData.count ?? 0)
      : selectedTab === NotificationsDeliveryTabEnum.routeNotifications
        ? (eventNotificationsData?.metaData.count ?? 0)
        : (data?.metaData.count ?? 0);

  const activeIsError =
    selectedTab === NotificationsDeliveryTabEnum.serviceAlerts
      ? serviceAlertsIsError
      : selectedTab === NotificationsDeliveryTabEnum.routeNotifications
        ? eventNotificationsIsError
        : isError;

  const hasActiveFilters =
    selectedTab === NotificationsDeliveryTabEnum.serviceAlerts
      ? Boolean(search) || Boolean(notificationsStatus)
      : selectedTab === NotificationsDeliveryTabEnum.oneTime
        ? Boolean(search) ||
          Boolean(notificationsType) ||
          notificationsDeliverySelect !== NotificationsDeliveryPartEnum.delivery
        : Boolean(search) || Boolean(notificationsType); // recurring & routeNotifications

  const { isEmpty, isTrueEmpty } = useListEmptyState(
    activeIsLoading,
    activeTotalCount,
    hasActiveFilters,
    activeIsError,
  );

  const clearFilters = () => {
    setSearch("");
    setPage(1);
    setNotificationsType(undefined);
    setNotificationsStatus(undefined);
    setNotificationsDeliverySelect(NotificationsDeliveryPartEnum.delivery);
    setNotificationsDelivery(
      notificationsDeliveryTabEnumFilterMap[selectedTab],
    );
    setSearchParams((prev) => {
      prev.delete("search");
      prev.delete("page");
      prev.delete("type");
      prev.delete("status");
      return prev;
    });
  };

  return (
    <SocketContextProvider currentPage="notifications">
      <DashboardContentLayout>
        <Navbar showAccountMenu>
          <HeaderItems>
            <HeaderItemsLeft
              breadcrumbs={[
                {
                  label: "Notification Center",
                  url: AdRoutes.notifications,
                },
              ]}
            />
            <HeaderItemsRight>
              <HeaderStandardActions
                onRefresh={
                  isTrueEmpty
                    ? undefined
                    : () => {
                        switch (selectedTab) {
                          case NotificationsDeliveryTabEnum.serviceAlerts:
                            serviceAlertsRefetch();
                            break;
                          default:
                            refetch();
                            break;
                        }
                      }
                }
                onAdd={() => navigate(AdRoutes.addNotification)}
                addButtonText="Create Notification"
              />
            </HeaderItemsRight>
          </HeaderItems>
        </Navbar>
        <Box sx={styles.tabSection}>
          <Tabs
            value={selectedTab}
            onChange={(_e, value) => {
              setSearchParams((params) => {
                params.set("tab", value);
                if (value === NotificationsDeliveryTabEnum.serviceAlerts) {
                  params.delete("type");
                  if (notificationsStatus) {
                    params.set("status", notificationsStatus);
                  }
                  if (serviceAlertSortOptions) {
                    params.set("sortBy", serviceAlertSortOptions.sortBy);
                    params.set("direction", serviceAlertSortOptions.direction);
                  } else {
                    params.delete("sortBy");
                    params.delete("direction");
                  }
                } else {
                  params.delete("status");
                  if (notificationsType) {
                    params.set("type", notificationsType);
                  }
                  if (sortOptions) {
                    params.set("sortBy", sortOptions.sortBy);
                    params.set("direction", sortOptions.direction);
                  } else {
                    params.delete("sortBy");
                    params.delete("direction");
                  }
                }
                return params;
              });
              setSelectedTab(value as NotificationsDeliveryTabEnum);
            }}
          >
            <Tab
              label="One-Time"
              value={NotificationsDeliveryTabEnum.oneTime}
              data-testid="one-time-tab"
            />
            <Tab
              label="Recurring"
              value={NotificationsDeliveryTabEnum.recurring}
              data-testid="recurring-tab"
            />
            <Tab
              label="Route Notifications"
              value={NotificationsDeliveryTabEnum.routeNotifications}
              data-testid="route-notifications-tab"
            />
            <Tab
              label="Service Alerts"
              value={NotificationsDeliveryTabEnum.serviceAlerts}
              data-testid="serive-alerts-tab"
            />
          </Tabs>
        </Box>
        {!isTrueEmpty && (
          <FilterRow
            tablePaginationProps={{
              limit,
              page,
              isLoading:
                selectedTab === NotificationsDeliveryTabEnum.serviceAlerts
                  ? serviceAlertsIsLoading
                  : selectedTab ===
                      NotificationsDeliveryTabEnum.routeNotifications
                    ? eventNotificationsIsLoading
                    : isLoading,
              count:
                selectedTab === NotificationsDeliveryTabEnum.serviceAlerts
                  ? (serviceAlertsData?.metaData.count ?? 10)
                  : selectedTab ===
                      NotificationsDeliveryTabEnum.routeNotifications
                    ? (eventNotificationsData?.metaData.count ?? 10)
                    : (data?.metaData.count ?? 10),
              setToPage: setPage,
              setLimit,
            }}
            search={search}
            onSearchChanged={onSearch}
            filters={[
              {
                enabled:
                  selectedTab !== NotificationsDeliveryTabEnum.serviceAlerts,
                placeholder: "Type",
                options: notificationsTypeSelectOptions,
                value: notificationsType,
                onChange: (value: string) => {
                  const v = sanitizeFilterValue(
                    value,
                    notificationsTypeEnumValues,
                  );
                  setNotificationsType(v);
                  setSearchParams((params) => {
                    v ? params.set("type", v) : params.delete("type");
                    return params;
                  });
                },
              },
              {
                enabled:
                  selectedTab === NotificationsDeliveryTabEnum.serviceAlerts,
                placeholder: "Status",
                options: notificationsServiceAlertStatusSelectOptions,
                value: notificationsStatus,
                onChange: (value: string) => {
                  let v = sanitizeFilterValue(
                    value,
                    notificationsServiceAlertStatusEnumValues,
                  );
                  if (v === NotificationsServiceAlertStatusEnum.all) {
                    v = undefined;
                  }
                  setNotificationsStatus(v);
                  setSearchParams((params) => {
                    v ? params.set("status", v) : params.delete("status");
                    return params;
                  });
                },
              },
              {
                enabled: selectedTab === "oneTime",
                placeholder: "Delivery",
                options: notificationsDeliveryPartSelectOptions,
                value: notificationsDeliverySelect ?? "",
                placeholdeShow: false,
                onChange: (value: string) => {
                  const v = sanitizeFilterValue(
                    value,
                    notificationsDeliveryPartEnumValues,
                  );
                  setNotificationsDeliverySelect(v);
                  if (v === NotificationsDeliveryPartEnum.delivery) {
                    setNotificationsDelivery([
                      NotificationsDeliveryEnum.instant,
                      NotificationsDeliveryEnum.scheduled,
                    ]);
                  } else {
                    setNotificationsDelivery(
                      v
                        ? [v as unknown as NotificationsDeliveryEnum]
                        : undefined,
                    );
                  }
                },
              },
            ]}
          />
        )}
        {isTrueEmpty ? (
          <Box mt="20px">
            <EmptyState
              title="No Notifications Yet"
              description="New notifications will appear here once you create them"
              actionLabel="Create Notification"
              actionStartIcon={<Add />}
              onAction={() => navigate(AdRoutes.addNotification)}
            />
          </Box>
        ) : isEmpty ? (
          <Box mt="20px">
            <EmptyState
              {...noResultsEmptyState("notifications", clearFilters)}
            />
          </Box>
        ) : (
          <>
            {selectedTab === NotificationsDeliveryTabEnum.serviceAlerts && (
              <NotificationsManagementServiceAlertsTable
                data={serviceAlertsData?.data ? serviceAlertsData.data : []}
                sortOptions={serviceAlertSortOptions}
                sortOptionsChange={setServiceAlertSortOptions}
                isLoading={serviceAlertsIsLoading}
                refetch={refetchServiceAlerts}
              />
            )}
            {selectedTab ===
              NotificationsDeliveryTabEnum.routeNotifications && (
              <RouteNotificationsTable
                data={
                  eventNotificationsData?.data
                    ? eventNotificationsData.data
                    : []
                }
                sortOptions={routeNotificationsSortOptions}
                sortOptionsChange={setRouteNotificationsSortOptions}
                isLoading={eventNotificationsIsLoading}
                refetch={eventNotificationsRefetch}
              />
            )}
            {(selectedTab === NotificationsDeliveryTabEnum.oneTime ||
              selectedTab === NotificationsDeliveryTabEnum.recurring) && (
              <NotificationsManagementTable
                data={data?.data ? data.data : []}
                sortOptions={sortOptions}
                sortOptionsChange={setSortOptions}
                isLoading={isLoading}
                selectedTab={selectedTab}
              />
            )}
          </>
        )}
      </DashboardContentLayout>
    </SocketContextProvider>
  );
};

const useStyles = makeSxStyles(() => ({
  tabSection: {
    marginTop: "28px",
    marginBottom: "40px",
    borderBottom: 1,
    borderColor: "divider",
    width: "max-content",
  },
}));

export default NotificationsManagementPage;
